import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import axios from 'axios';
import BlogSEO from '../components/BlogSEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      // Fetch only published posts
      const response = await axios.get(`${API_URL}/api/posts?status=published`);
      setPosts(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      setLoading(false);
    }
  };

  const calculateReadTime = (content) => {
    const wordsPerMinute = 200;
    const words = content.split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} min read`;
  };

  return (
    <div>
      {/* SEO Component */}
      <BlogSEO isBlogListing={true} allPosts={posts} />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16" style={{ background: 'var(--bg-section)' }}>
        <div className="container text-center">
          <h1 className="heading-1 mb-6">Pest Control Resources & Tips</h1>
          <p className="body-large max-w-3xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            Expert advice, seasonal tips, and pest prevention guides from New York's eco-friendly pest control specialists
          </p>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-20">
        <div className="container">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>Loading blog posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>No published blog posts yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {posts.map(post => (
                <article key={post.id} className="product-card card-hover-lift">
                  <div className="mb-4">
                    {post.featured_image_url ? (
                      <img 
                        src={post.featured_image_url} 
                        alt={post.title}
                        className="w-full h-48 rounded-lg object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="w-full h-48 rounded-lg flex items-center justify-center" style={{ background: 'var(--bg-section)', display: post.featured_image_url ? 'none' : 'flex' }}>
                      <span className="body-small" style={{ color: 'var(--text-muted)' }}>Article Image</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 mb-3">
                    <span className="caption px-3 py-1 rounded-full" style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)' }}>
                      {post.status}
                    </span>
                    <div className="flex items-center gap-2 caption" style={{ color: 'var(--text-muted)' }}>
                      <Calendar size={14} />
                      {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                    <div className="flex items-center gap-2 caption" style={{ color: 'var(--text-muted)' }}>
                      <Clock size={14} />
                      {calculateReadTime(post.content)}
                    </div>
                  </div>
                  
                  <h2 className="heading-3 mb-3">
                    <Link to={`/blog/${post.id}`} className="hover:underline">
                      {post.title}
                    </Link>
                  </h2>
                  
                  <p className="body-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
                    {post.content.substring(0, 150).replace(/##|\*/g, '')}...
                  </p>
                  
                  <div className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                    By {post.author}
                  </div>
                  
                  <Link to={`/blog/${post.id}`} className="flex items-center gap-2" style={{ color: 'var(--accent-text)' }}>
                    <span className="body-small font-semibold">Read More</span>
                    <ArrowRight size={16} />
                  </Link>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Browse by Category</h2>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            {['Termites', 'Rodents', 'Bed Bugs', 'Eco-Friendly', 'Commercial', 'Prevention'].map(category => (
              <button
                key={category}
                className="btn-secondary"
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;